"use client";

import Link from "next/link";
import { HiArrowRight } from "react-icons/hi2";
import { getProgramSummaries } from "@/content/programs";
import { ProgramInfoCard } from "@/components/programs/ProgramInfoCard";
import { HomeSectionHeader } from "@/components/home/HomeSectionHeader";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import type { HomeFeaturedProgramsContent } from "@/lib/api-types";
import { DEFAULT_HOME_FEATURED_PROGRAMS } from "@/lib/site-content-fallbacks";
import { cn } from "@/lib/utils";

type ProgramCardSummary = {
  slug: string;
  title: string;
  description: string;
  image: string;
};

interface HomeFeaturedProgramsProps {
  summaries?: ProgramCardSummary[];
  featured?: HomeFeaturedProgramsContent;
}

export function HomeFeaturedPrograms({
  summaries: cmsSummaries,
  featured = DEFAULT_HOME_FEATURED_PROGRAMS,
}: HomeFeaturedProgramsProps) {
  const staticSummaries = getProgramSummaries();
  const bySlug = new Map<string, ProgramCardSummary>();
  for (const p of staticSummaries) {
    bySlug.set(p.slug, {
      slug: p.slug,
      title: p.title,
      description: p.description,
      image: p.image,
    });
  }
  for (const p of cmsSummaries ?? []) {
    bySlug.set(p.slug, p);
  }

  const featuredPrograms = featured.slugs
    .map((slug) => bySlug.get(slug))
    .filter((p): p is ProgramCardSummary => p !== undefined);

  return (
    <section
      id="programs"
      className="home-charity-section bg-[var(--charity-ivory)] py-16 md:py-20"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <HomeSectionHeader
          eyebrow={featured.eyebrow}
          title={featured.title}
          subtitle={featured.subtitle}
        />
        <Stagger className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {featuredPrograms.map((program, index) => (
            <StaggerItem key={program.slug} className="h-full">
              <ProgramInfoCard
                slug={program.slug}
                title={program.title}
                description={program.description}
                image={program.image}
                accentIndex={index}
              />
            </StaggerItem>
          ))}
        </Stagger>
        <p className="mt-10 text-center">
          <Link
            href={featured.ctaHref}
            className={cn(
              "group inline-flex items-center justify-center gap-2 rounded-2xl",
              "border border-[var(--charity-green-dark)] bg-[var(--charity-green-dark)] px-7 py-3.5",
              "text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,94,32,0.25)]",
              "transition-all duration-300 hover:-translate-y-0.5 hover:bg-[var(--charity-green)]",
              "hover:shadow-[0_12px_32px_rgba(46,125,50,0.3)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--charity-green)] focus-visible:ring-offset-2"
            )}
          >
            {featured.ctaLabel}
            <HiArrowRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              aria-hidden
            />
          </Link>
        </p>
      </div>
    </section>
  );
}
